import { createColumnHelper } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { InferType, object } from 'yup';

import { Role } from '@ndvr/auth-api';
import { PlannedWithdrawalEvent, WithdrawalStatus } from '@ndvr/portfolio-api';

import { CopiableGuid } from '@/components/CopiableGuid';
import { FormSheetContent } from '@/components/FormSheet';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, useSheetClose } from '@/components/ui/sheet';
import { accountRoute, formatCurrency, formatDate, formatDateTime, titleCase, useHouseholdRoutePaths } from 'utils';
import { useUserHasRole } from 'views/admin/dev-queries';
import { DrawerMessage } from 'views/portfolio-engine/DrawerMessage';
import { useSavePlannedWithdrawal, useWithdrawalEvents } from 'views/portfolio/cash-flow-queries';
import {
    CondensedTable,
    DataGrid,
    FormLabel,
    FormSection,
    HorizontalLayout,
    PageSpinner,
    requiredStringValidator,
    StaffOrPersonName,
    TextLink,
    Title,
    Toast,
    Tooltip,
} from 'widgets';
import { FormButtonSelect } from 'widgets/form/input/FormButtonSelect';

import { RequestReason } from '../../../cip/_openapi/portfolio-optimizer';
import { useCreateInvestmentRequest } from '../investment-request/investment-request-queries';
import { withdrawalStatusToIcon } from './typeHelper';
import { decodeWithdrawalId, useWithdrawalData } from './withdrawal-hooks';

const formSchema = object({
    withdrawalStatus: requiredStringValidator.oneOf(Object.values(WithdrawalStatus)),
});

type FormType = InferType<typeof formSchema>;

const columnHelper = createColumnHelper<PlannedWithdrawalEvent>();
const columns = [
    columnHelper.accessor('editedAt', {
        cell: ({ getValue }) => <Tooltip tooltip={formatDateTime(getValue())}>{formatDate(getValue())}</Tooltip>,
        sortingFn: 'datetime',
    }),
    columnHelper.accessor('withdrawalStatus', {
        cell: ({ getValue }) => titleCase(getValue()),
    }),
    columnHelper.accessor('editedBy', {
        cell: ({ getValue }) => {
            const editedBy = getValue();
            return editedBy ? <StaffOrPersonName guid={editedBy} /> : 'System';
        },
    }),
];

export const ProcessWithdrawalDrawer = () => {
    const navigate = useNavigate();

    return (
        <Sheet defaultOpen onExitComplete={() => navigate('/trading/withdrawals', { replace: true })}>
            <SheetContent aria-describedby={undefined} layout="scroll-body" size="md">
                <ProcessWithdrawalContent />
            </SheetContent>
        </Sheet>
    );
};

const ProcessWithdrawalContent = () => {
    const closeSheet = useSheetClose();
    const { withdrawalId } = useParams<{ withdrawalId: string }>();
    const location = useLocation();

    // Parse and validate withdrawalId
    const decodedId = withdrawalId ? decodeWithdrawalId(withdrawalId) : null;
    const isValidWithdrawalId = decodedId !== null;

    // Role checks to determine if user should see all tenants or only current tenant
    const iopsPortalRole = useUserHasRole(Role.IOPS_PORTAL_ACCESS);
    const hasCustodyAccess = !!iopsPortalRole.data;
    const isRoleCheckPending = iopsPortalRole.isPending;

    // Optimize data fetching by reusing the list page's date range if available
    // When navigating from the list page, we pass the date range via React Router state.
    // This allows us to use the SAME React Query cache key as the list page, resulting in:
    // - Zero network requests (instant data from cache)
    // - Zero transformation overhead (data is already transformed)
    // If not navigated from list (e.g., direct link), we fall back to fetching the specific date.
    const listDateRange = (location.state as { listDateRange?: { startDate: Date; endDate: Date } })?.listDateRange;
    const withdrawalDate = decodedId?.withdrawalDate ? new Date(decodedId.withdrawalDate) : new Date();
    const isValidDate = !isNaN(withdrawalDate.getTime());
    const startDate =
        listDateRange?.startDate || (isValidDate ? withdrawalDate : new Date(new Date().getFullYear(), 0, 1));
    const endDate =
        listDateRange?.endDate || (isValidDate ? withdrawalDate : new Date(new Date().getFullYear(), 11, 31));

    const { getWithdrawalById, isLoading: isLoadingList } = useWithdrawalData({
        startDate,
        endDate,
        filterByTenant: !hasCustodyAccess,
    });

    // Always call hooks - use enabled flag to control fetching
    const { PortfolioPageRoutePath } = useHouseholdRoutePaths();
    const { data: withdrawalEvents = [], isPending: withdrawalEventsPending } = useWithdrawalEvents(
        decodedId?.cashFlowConfigGUID || '',
        decodedId?.withdrawalDate || '',
    );
    const { mutate: savePlannedWithdrawal, isPending: savePending } = useSavePlannedWithdrawal();
    const { mutate: createInvestmentRequest, isPending: createInvestmentRequestPending } = useCreateInvestmentRequest();
    const isSaving = savePending || createInvestmentRequestPending;

    const withdrawal = isValidWithdrawalId
        ? getWithdrawalById(decodedId.cashFlowConfigGUID, decodedId.withdrawalDate)
        : undefined;

    const formDefaultValues = useMemo(
        () => ({
            withdrawalStatus: withdrawal?.withdrawalStatus ?? WithdrawalStatus.UPCOMING,
        }),
        [withdrawal?.withdrawalStatus],
    );

    // Handle invalid withdrawal ID early
    if (!isValidWithdrawalId) {
        return (
            <DrawerMessage title="Invalid Withdrawal">
                <Title content>Invalid Withdrawal</Title>
                <div>The withdrawal ID is invalid.</div>
                <Button onClick={closeSheet}>Back to List</Button>
            </DrawerMessage>
        );
    }

    // Handle loading state
    if (isLoadingList || isRoleCheckPending) {
        return (
            <DrawerMessage title="Loading withdrawal">
                <PageSpinner />
                <div>Loading withdrawal...</div>
            </DrawerMessage>
        );
    }

    // Handle not found or no permission
    if (!withdrawal) {
        return (
            <DrawerMessage title="Access Denied">
                <Title content>Access Denied</Title>
                <div>You don&apos;t have permission to view this withdrawal.</div>
                <Button onClick={closeSheet}>Back to List</Button>
            </DrawerMessage>
        );
    }

    const getAllowedWithdrawalTransitions = (currentStatus: WithdrawalStatus): WithdrawalStatus[] => {
        switch (currentStatus) {
            case WithdrawalStatus.UPCOMING:
                return [
                    WithdrawalStatus.UPCOMING,
                    WithdrawalStatus.IN_PROGRESS,
                    WithdrawalStatus.CASH_AVAILABLE,
                    WithdrawalStatus.COMPLETED,
                    WithdrawalStatus.CANCELED,
                ];
            case WithdrawalStatus.IN_PROGRESS:
                return [
                    WithdrawalStatus.IN_PROGRESS,
                    WithdrawalStatus.UPCOMING,
                    WithdrawalStatus.CASH_AVAILABLE,
                    WithdrawalStatus.COMPLETED,
                    WithdrawalStatus.CANCELED,
                ];
            case WithdrawalStatus.CASH_AVAILABLE:
                return [WithdrawalStatus.CASH_AVAILABLE, WithdrawalStatus.COMPLETED, WithdrawalStatus.CANCELED];
            case WithdrawalStatus.COMPLETED:
                return [WithdrawalStatus.COMPLETED, WithdrawalStatus.CANCELED];
            default:
                return [currentStatus]; // For other statuses, only allow staying in same state
        }
    };

    const allowedWithdrawalStatuses = getAllowedWithdrawalTransitions(withdrawal.withdrawalStatus);

    const handleSubmit = (formValues: FormType, close: () => void) => {
        const isMovingToInProgress =
            formValues.withdrawalStatus === WithdrawalStatus.IN_PROGRESS &&
            withdrawal.withdrawalStatus !== WithdrawalStatus.IN_PROGRESS;

        savePlannedWithdrawal(
            {
                cashFlowConfigGUID: withdrawal.cashFlowConfigGUID,
                withdrawalDate: withdrawal.withdrawalDate,
                withdrawalStatus: formValues.withdrawalStatus,
            },
            {
                onSuccess: () => {
                    // If successfully moved to IN_PROGRESS, create investment request
                    if (isMovingToInProgress && withdrawal.accountGUID) {
                        createInvestmentRequest(
                            {
                                investmentRequestCreatePayload: {
                                    portfolioGuid: withdrawal.accountGUID,
                                    reason: RequestReason.WITHDRAWAL,
                                    description: `Urgent withdrawal - ${formatCurrency(withdrawal.withdrawalAmount)}`,
                                },
                            },
                            {
                                onSuccess: () => {
                                    Toast.success('Investment request created for ad-hoc withdrawal.');
                                    close();
                                },
                                onError: (error) => {
                                    Toast.forError(
                                        error,
                                        'Withdrawal status updated but failed to create investment request.',
                                    );
                                    close();
                                },
                            },
                        );
                    } else {
                        close();
                    }
                },
                onError: (error) => {
                    Toast.forError(error, 'Failed to update withdrawal status. Please try again.');
                },
            },
        );
    };

    return (
        <FormSheetContent
            title="Process Withdrawal"
            formKey={`${withdrawal.cashFlowConfigGUID}-${withdrawal.withdrawalDate}`}
            formSchema={formSchema}
            defaultValues={formDefaultValues}
            pending={isSaving}
            onSubmit={handleSubmit}
        >
            <FormSection>
                <DataGrid
                    values={[
                        { label: 'Household Name', value: withdrawal.householdName },
                        { label: 'Client Name', value: withdrawal.clientName },
                        {
                            label: 'Portfolio Name',
                            value: (
                                <TextLink
                                    to={PortfolioPageRoutePath(
                                        withdrawal.householdGUID || '',
                                        withdrawal.accountGroupGUID || '',
                                    )}
                                >
                                    {withdrawal.portfolioName}
                                </TextLink>
                            ),
                        },
                        {
                            label: 'Account Name',
                            value: (
                                <TextLink to={accountRoute(withdrawal.accountGUID || '')}>
                                    {withdrawal.accountName}
                                </TextLink>
                            ),
                        },
                        {
                            label: 'Account Guid',
                            value: (
                                <CopiableGuid
                                    guid={withdrawal.accountGUID || ''}
                                    showFullGuid={true}
                                    showBorder={false}
                                />
                            ),
                        },

                        {
                            label: 'Custody Id',
                            value: (
                                <>
                                    <CopiableGuid
                                        guid={withdrawal.custodyId || ''}
                                        showFullGuid={true}
                                        showBorder={false}
                                    />{' '}
                                    ({titleCase(withdrawal.custodyType)})
                                </>
                            ),
                        },
                        { label: 'Withdrawal Date', value: formatDate(withdrawal.withdrawalDate) },
                        {
                            label: 'Withdrawal Amount',
                            value: formatCurrency(withdrawal.withdrawalAmount),
                        },
                    ]}
                    alignment="center"
                />
            </FormSection>
            <FormSection hasDivider>
                <FormButtonSelect
                    name="withdrawalStatus"
                    label="Withdrawal Status"
                    options={Object.values(WithdrawalStatus).map((status) => ({
                        value: status,
                        disabled: !allowedWithdrawalStatuses.includes(status),
                    }))}
                    renderFn={(option, selected) => (
                        <HorizontalLayout $width="100%" $gap={12}>
                            {withdrawalStatusToIcon(option.value as WithdrawalStatus, selected)}{' '}
                            {titleCase(option.value as string)}
                        </HorizontalLayout>
                    )}
                />
            </FormSection>
            <FormSection hasDivider>
                <FormLabel>History</FormLabel>
                <CondensedTable
                    columns={columns}
                    data={withdrawalEvents}
                    sort={{ col: 'editedAt', desc: true }}
                    isLoading={withdrawalEventsPending}
                    hideHeader
                />
            </FormSection>
        </FormSheetContent>
    );
};
